// Platform SSO cookie helpers.
//
// The Lambda@Edge gate fronting the support site (and any future
// CloudFront-fronted authenticated property) cannot reach into the
// SPA's localStorage to find an access token. To make
// `support.goodstewardaba.com` work via direct browser navigation
// from any platform app, the SPA additionally writes its access
// token to a cookie scoped to `.goodstewardaba.com` so the gate sees
// it on the request.
//
// Constraints worth knowing:
//   - SPA-set cookies cannot be HttpOnly. The cookie is therefore
//     readable by JS on every subdomain of `.goodstewardaba.com`.
//     This is a deliberate, accepted trade-off until the OAuth
//     code-for-token exchange moves server-side; tracked in
//     docs/security-hardening-plan.md §1.1.
//   - The cookie value is the access token verbatim. The gate's
//     verifier expects a Cognito-issued access JWT.
//   - Domain must include the leading dot to span subdomains
//     (.goodstewardaba.com), per RFC 6265 host-matching rules in
//     practice for cross-subdomain reads.
//
// Local-dev callers pass `domain: undefined` (or omit it) and the
// helpers no-op. The stub IdP doesn't need a cookie — local apps
// stay on `localhost`, no cross-subdomain navigation, and the gate
// isn't running.

const PLATFORM_COOKIE_NAME = 'gs_access_token';

function doc() {
    if (typeof document === 'undefined') {
        throw new Error('document is not available');
    }
    return document;
}

/**
 * Write the platform SSO cookie. No-op if `domain` is unset (local dev).
 *
 * @param {string} token — the access token to write.
 * @param {object} [opts]
 * @param {string} [opts.domain] — cookie Domain attribute, e.g. ".goodstewardaba.com".
 *   When unset or empty, the function returns without writing.
 * @param {number} [opts.expiresAt] — ms-since-epoch token expiry. Translated
 *   to a Max-Age attribute. Without it, the cookie is a session cookie
 *   (cleared on browser close).
 * @param {string} [opts.path] — defaults to '/'.
 * @param {boolean} [opts.secure] — defaults to true. Set false only for
 *   plain-HTTP local dev, which won't have a domain set anyway.
 * @param {'Lax'|'Strict'} [opts.sameSite] — defaults to 'Lax' so cross-
 *   subdomain top-level navigation (clicking a Help link) carries the
 *   cookie. 'Strict' would block that.
 */
export function writePlatformCookie(token, opts = {}) {
    if (!token) return;
    const domain = opts.domain;
    if (!domain) return;
    const path = opts.path || '/';
    const sameSite = opts.sameSite || 'Lax';
    const secure = opts.secure !== false;
    const parts = [
        `${PLATFORM_COOKIE_NAME}=${encodeURIComponent(token)}`,
        `Domain=${domain}`,
        `Path=${path}`,
        `SameSite=${sameSite}`,
    ];
    if (secure) parts.push('Secure');
    if (opts.expiresAt) {
        const maxAgeSec = Math.max(0, Math.floor((opts.expiresAt - Date.now()) / 1000));
        parts.push(`Max-Age=${maxAgeSec}`);
    }
    doc().cookie = parts.join('; ');
}

/**
 * Clear the platform SSO cookie. No-op if `domain` is unset.
 * Domain + Path must match the values used at write time, or the
 * browser keeps the original cookie alive.
 *
 * @param {object} [opts]
 * @param {string} [opts.domain]
 * @param {string} [opts.path]
 * @param {boolean} [opts.secure]
 * @param {'Lax'|'Strict'} [opts.sameSite]
 */
export function clearPlatformCookie(opts = {}) {
    const domain = opts.domain;
    if (!domain) return;
    const path = opts.path || '/';
    const sameSite = opts.sameSite || 'Lax';
    const secure = opts.secure !== false;
    const parts = [
        `${PLATFORM_COOKIE_NAME}=`,
        `Domain=${domain}`,
        `Path=${path}`,
        `SameSite=${sameSite}`,
        'Max-Age=0',
    ];
    if (secure) parts.push('Secure');
    doc().cookie = parts.join('; ');
}

/** Cookie name. Exported for tests + doc references; do not parameterize. */
export const platformCookieName = PLATFORM_COOKIE_NAME;
